import { AxiosResponse } from 'axios';
import { FormModel } from '../../models/form/form';
import { OrganizationModel } from '../../models/organization/organization';
import { SubmissionData, Submissions } from '../../models/submission/submission';
import { getAxiosPublicInstance, getAxiosSecureInstance } from '../../utilities/AxiosClient';

interface FormDataWithContentStringModel extends Omit<FormModel, 'content'> {
  content: string;
}

type FormDataWithoutContentModel = Omit<FormModel, 'content'>;

interface SubmissionWithDataContentStringModel extends SubmissionData {
  form: FormModel;
  data: string;
}

interface FormTemplateWithDataContentStringModel {
  id: string;
  name: string;
  content: string;
  paymentModes: string | null;
}

export interface Coupon {
  code: string;
  discountType: 'percent' | 'amount';
  discountValue: number;
}

export type getFormsResponseModel = FormDataWithoutContentModel[];
export function getForms(
  organizationId: string,
  page: number,
  pageSize: number,
  state: string,
): Promise<AxiosResponse<getFormsResponseModel>> {
  return getAxiosSecureInstance().get(
    `/api/organizations/${organizationId}/forms?page=${page}&pageSize=${pageSize}&state=${state}`,
  );
}

export type createFormResponseModel = FormDataWithContentStringModel;
export function createForm(organizationId: string, form: FormModel): Promise<AxiosResponse<createFormResponseModel>> {
  const formData: FormDataWithContentStringModel = {
    ...form,
    content: JSON.stringify(form.content),
    paymentModes: form.paymentModes,
  };
  return getAxiosSecureInstance().post(`/api/organizations/${organizationId}/forms`, formData);
}

export function createFormFromTemplate(
  organizationId: string,
  form: FormModel,
  templateId: string,
  avatarFile: File,
): Promise<AxiosResponse<createFormResponseModel>> {
  const formData: FormDataWithContentStringModel = {
    ...form,
    content: JSON.stringify(form.content),
    paymentModes: form.paymentModes,
  };

  const formBody: FormData = new FormData();
  formBody.append('form', JSON.stringify(formData));
  formBody.append('avatar', avatarFile as File);

  return getAxiosSecureInstance().post(`/api/organizations/${organizationId}/forms/templates/${templateId}`, formBody);
}

export function cloneForm(
  organizationId: string,
  formId: string,
  newForm: { id: string; name: string },
): Promise<AxiosResponse<createFormResponseModel>> {
  return getAxiosSecureInstance().post(`/api/organizations/${organizationId}/forms/${formId}/clone`, newForm);
}

export type updateFormResponseModel = FormDataWithContentStringModel;
export function updateForm(
  organizationId: string,
  form: FormModel,
  avatarFile: File | null,
): Promise<AxiosResponse<updateFormResponseModel>> {
  const formData: FormDataWithContentStringModel = {
    ...form,
    content: JSON.stringify(form.content),
    paymentModes: form.paymentModes,
    discountCodes: form.discountCodes,
  };

  const formBody: FormData = new FormData();
  formBody.append('form', JSON.stringify(formData));

  if (avatarFile != null) {
    formBody.append('avatar', avatarFile);
  }

  return getAxiosSecureInstance().put(`/api/organizations/${organizationId}/forms/${form.id}`, formBody);
}

export type deleteFormResponseModel = number;
export function deleteForm(organizationId: string, formId: string): Promise<AxiosResponse<deleteFormResponseModel>> {
  return getAxiosSecureInstance().post(`/api/organizations/${organizationId}/forms/${formId}/delete`);
}

export function archiveForm(organizationId: string, formId: string): Promise<AxiosResponse<number>> {
  return getAxiosSecureInstance().post(`/api/organizations/${organizationId}/forms/${formId}/archive`);
}

export function unarchiveForm(organizationId: string, formId: string): Promise<AxiosResponse<number>> {
  return getAxiosSecureInstance().post(`/api/organizations/${organizationId}/forms/${formId}/unarchive`);
}

export type getFormByIdResponseModel = FormDataWithContentStringModel;
export function getFormById(
  organizationId: string,
  formId: string | undefined,
): Promise<AxiosResponse<getFormByIdResponseModel>> {
  return getAxiosSecureInstance().get(`/api/organizations/${organizationId}/forms/${formId}`);
}

export type getFormDetailsByIdReponseModel = { form: FormDataWithContentStringModel; organization: OrganizationModel };
export function getFormDetailsById(
  organizationId: string,
  formId: string | undefined,
): Promise<AxiosResponse<getFormDetailsByIdReponseModel>> {
  return getAxiosSecureInstance().get(`/api/organizations/${organizationId}/forms/${formId}/details`);
}

export function getPublicFormDetailsById(
  formId: string | undefined,
): Promise<AxiosResponse<getFormDetailsByIdReponseModel>> {
  return getAxiosPublicInstance().get(`/api/public/forms/${formId}/details`);
}

export function getSubmissionList(
  organizationId: string,
  formId: string,
  page: number,
  pageSize: number,
): Promise<AxiosResponse> {
  return getAxiosSecureInstance().get(
    `/api/organizations/${organizationId}/forms/${formId}/submissions/details?page=${page}&pageSize=${pageSize}`,
  );
}

export function getSubmission(
  organizationId: string,
  formId: string,
  formSubmissionId: string,
): Promise<AxiosResponse> {
  return getAxiosSecureInstance().get(
    `/api/organizations/${organizationId}/forms/${formId}/submissions/${formSubmissionId}/details`,
  );
}

export function postSubmission(
  organizationId: string,
  formId: string | undefined,
  formData: FormData,
): Promise<AxiosResponse> {
  return getAxiosSecureInstance().post(`/api/organizations/${organizationId}/forms/${formId}/submissions`, formData);
}
export function updateSubmission(
  organizationId: string,
  formId: string | undefined,
  formData: FormData,
  submissionId: string,
): Promise<AxiosResponse> {
  return getAxiosSecureInstance().put(
    `/api/organizations/${organizationId}/forms/${formId}/submissions/${submissionId}`,
    formData,
  );
}

export function deleteSubmission(
  organizationId: string,
  formId: string | undefined,
  formSubmissionId: string,
  paymentIntentId: string | undefined,
): Promise<AxiosResponse> {
  let url = `/api/organizations/${organizationId}/forms/${formId}/submissions/${formSubmissionId}`;

  if (paymentIntentId) {
    url += `?paymentIntentId=${paymentIntentId}`;
  }

  return getAxiosSecureInstance().delete(url);
}

export type getSubmissionByIdResponseModel = SubmissionWithDataContentStringModel;
export function getSubmissionById(
  submissionId: string | undefined,
  formId: string | undefined,
): Promise<AxiosResponse<getSubmissionByIdResponseModel>> {
  return getAxiosSecureInstance(true).get(`/api/submissions/${formId}/${submissionId}`);
}

export type getSubmissions = SubmissionWithDataContentStringModel[];
export type getSubmissionsLists = Submissions[];
export function getSubmissionListById(formId: string | undefined): Promise<AxiosResponse<getSubmissions>> {
  return getAxiosSecureInstance(true).get(`/api/submissions/${formId}`);
}

export type getFormTemplatesResponseModel = FormTemplateWithDataContentStringModel[];
export function getFormTemplates(
  organizationId: string,
  page = 1,
  pageSize = 12,
): Promise<AxiosResponse<getFormTemplatesResponseModel>> {
  return getAxiosSecureInstance().get(
    `/api/organizations/${organizationId}/templates?page=${page}&pageSize=${pageSize}`,
  );
}

export function validateDiscountCode(
  organizationId: string,
  formId: string,
  code: string,
): Promise<AxiosResponse<Coupon>> {
  return getAxiosSecureInstance().post(`/api/organizations/${organizationId}/forms/${formId}/discount/validate`, {
    code,
  });
}
